/**
 * 確認②: 場所を特定しきれなかった候補の修正
 *
 * 自動で確定した分はたたんでおき、判断が要る分だけを出す。
 * ここでの手動検索は Google の枠を 1 回消費するので、ボタンを押したときにだけ走らせる。
 */

import { Check, Loader2, Search, X } from 'lucide-react'
import { useState } from 'react'

import { searchRallyPlaces } from '@/api/services/stamp-rally'
import type { CandidatePatchParams, ImportCandidate, PlaceSearchResult } from '@/api/types/stamp-rally'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

type Props = {
  candidates: ImportCandidate[]
  isSubmitting: boolean
  onApply: (patches: CandidatePatchParams[]) => void
}

const isSettled = (candidate: ImportCandidate) =>
  candidate.resolve_status === 'resolved' || candidate.resolve_status === 'manual'

export const ImportCandidateList = ({ candidates, isSubmitting, onApply }: Props) => {
  // 確定内容はローカルに溜めて、登録時にまとめて送る
  const [overrides, setOverrides] = useState<Map<number, CandidatePatchParams>>(new Map())
  const [searchIndex, setSearchIndex] = useState<number | null>(null)
  const [keyword, setKeyword] = useState('')
  const [results, setResults] = useState<PlaceSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const indexed = candidates.map((candidate, index) => ({ candidate, index }))
  const approved = indexed.filter(({ candidate }) => candidate.approval === 'approved')
  const settled = approved.filter(({ candidate }) => isSettled(candidate))
  const pending = approved.filter(({ candidate }) => !isSettled(candidate))

  const setOverride = (index: number, patch: CandidatePatchParams) => {
    setOverrides((prev) => new Map(prev).set(index, patch))
    setSearchIndex(null)
    setResults([])
  }

  const handleSearch = async (index: number) => {
    const query = keyword.trim()
    if (!query) return
    setIsSearching(true)
    try {
      setResults(await searchRallyPlaces(query))
    } finally {
      setIsSearching(false)
    }
    setSearchIndex(index)
  }

  const openSearch = (index: number, defaultKeyword: string) => {
    setSearchIndex(index)
    setKeyword(defaultKeyword)
    setResults([])
  }

  const decidedCount = settled.length + pending.filter(({ index }) => overrides.get(index)?.google_place_id).length

  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-4 py-3">
        <p className="text-sm">
          <span className="font-semibold tabular-nums">{decidedCount}</span> 件を登録します
        </p>
        {pending.length > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            場所を特定できなかった {pending.length} 件は、選ばなければ登録されません。
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {settled.length > 0 && (
          <Collapsible>
            <CollapsibleTrigger className="w-full rounded-xl border bg-card px-4 py-3 text-left text-sm">
              {settled.length} 件は自動で確定しました
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-1">
              {settled.map(({ candidate, index }) => (
                <div key={index} className="rounded-lg border bg-card px-3 py-2 text-sm">
                  <p className="font-medium">{candidate.resolved?.display_name ?? candidate.name}</p>
                  <p className="text-xs text-muted-foreground">{candidate.resolved?.address ?? '住所不明'}</p>
                  {candidate.already_registered && (
                    <p className="mt-1 text-xs text-muted-foreground">すでに登録済みのため飛ばします</p>
                  )}
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}

        {pending.map(({ candidate, index }) => {
          const override = overrides.get(index)
          const isExcluded = override?.approval === 'excluded'
          const picked = override?.google_place_id ? override : null

          return (
            <div key={index} className={cn('rounded-xl border bg-card p-3', isExcluded && 'opacity-50')}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium">{candidate.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {candidate.group_name ?? 'エリア不明'}
                    {candidate.resolve_status === 'unresolved' && ' / 見つかりませんでした'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="登録しない"
                  onClick={() => setOverride(index, { index, approval: 'excluded' })}
                >
                  <X className="size-4" />
                </Button>
              </div>

              {picked && (
                <p className="mt-2 flex items-center gap-1 text-sm text-primary">
                  <Check className="size-4" />
                  {picked.display_name}
                </p>
              )}

              {!picked && !isExcluded && candidate.resolved && (
                <div className="mt-2 space-y-1">
                  {[
                    {
                      google_place_id: candidate.resolved.google_place_id,
                      display_name: candidate.resolved.display_name,
                      address: candidate.resolved.address,
                      latitude: candidate.resolved.latitude,
                      longitude: candidate.resolved.longitude,
                    },
                    ...candidate.resolved.alternatives,
                  ].map((option) => (
                    <button
                      key={option.google_place_id ?? option.display_name}
                      type="button"
                      className="w-full rounded-lg border p-2 text-left text-sm"
                      onClick={() =>
                        setOverride(index, {
                          index,
                          approval: 'approved',
                          google_place_id: option.google_place_id,
                          display_name: option.display_name,
                          address: option.address ?? null,
                          latitude: option.latitude ?? null,
                          longitude: option.longitude ?? null,
                        })
                      }
                    >
                      <span className="block font-medium">{option.display_name}</span>
                      <span className="block text-xs text-muted-foreground">{option.address ?? '住所不明'}</span>
                    </button>
                  ))}
                </div>
              )}

              {searchIndex === index ? (
                <div className="mt-2 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      placeholder="スポット名で検索"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label="検索"
                      disabled={isSearching}
                      onClick={() => void handleSearch(index)}
                    >
                      {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
                    </Button>
                  </div>
                  {results.map((result) => (
                    <button
                      key={result.google_place_id ?? result.name}
                      type="button"
                      className="w-full rounded-lg border p-2 text-left text-sm"
                      onClick={() =>
                        setOverride(index, {
                          index,
                          approval: 'approved',
                          google_place_id: result.google_place_id,
                          display_name: result.name,
                          address: result.address,
                          latitude: result.latitude,
                          longitude: result.longitude,
                        })
                      }
                    >
                      <span className="block font-medium">{result.name}</span>
                      <span className="block text-xs text-muted-foreground">{result.address ?? '住所不明'}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full"
                  onClick={() => openSearch(index, `${candidate.group_name ?? ''} ${candidate.name}`.trim())}
                >
                  <Search className="mr-2 size-4" />
                  別の場所を検索
                </Button>
              )}
            </div>
          )
        })}
      </div>

      <div className="border-t p-3">
        <Button
          className="w-full"
          disabled={decidedCount === 0 || isSubmitting}
          onClick={() => onApply([...overrides.values()])}
        >
          {isSubmitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Check className="mr-2 size-4" />}
          {decidedCount} 件を登録する
        </Button>
      </div>
    </div>
  )
}
